import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import {
  Box,
  Container,
  CircularProgress,
  Alert,
  Grid,
  Typography,
  Button,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Snackbar,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ReceiptIcon from '@mui/icons-material/Receipt';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import MuiPageHeader from '../../components/mui/MuiPageHeader';
import MuiCard from '../../components/mui/MuiCard';
import MuiStatusChip from '../../components/mui/MuiStatusChip';
import { getPayments, patchPayment, deletePayment } from '../../helpers/api_helper';

const MuiPaymentOverview = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();

  const [paymentDetails, setPaymentDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Menu state
  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);

  // Delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  document.title = t('Payment Details') + ' | flexflow - React Admin & Dashboard Template';

  useEffect(() => {
    const fetchPaymentDetails = async () => {
      try {
        const response = await getPayments(id);
        if (response && response[0]) {
          setPaymentDetails(response[0]);
        } else {
          setError(t('No payment details found.'));
        }
      } catch (err) {
        setError(t('Error loading payment data.'));
      } finally {
        setLoading(false);
      }
    };

    fetchPaymentDetails();
  }, [id, t]);

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = () => {
    handleMenuClose();
    // Initialize edit data from current payment details
    setEditData({
      bound: paymentDetails.bound || 'Inbound',
      type: paymentDetails.type || '',
      ammount: paymentDetails.ammount || '',
      series: paymentDetails.series || '',
      date: paymentDetails.date ? moment(paymentDetails.date) : moment(),
      bankname: paymentDetails.bankname || '',
      issuer: paymentDetails.issuer || '',
      expire_date: paymentDetails.expire_date ? moment(paymentDetails.expire_date) : null,
      iban: paymentDetails.iban || '',
      receipt_code: paymentDetails.receipt_code || '',
    });
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditData({});
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      const payload = {
        resource: [{
          id: parseInt(id),
          bound: editData.bound,
          type: editData.type,
          ammount: editData.ammount,
          series: editData.series,
          date: editData.date,
          entity_id: paymentDetails.entity_id,
          entity_name: paymentDetails.entity_name,
          refcode: paymentDetails.refcode,
          ...(editData.type === 'CHECK' && {
            bankname: editData.bankname,
            expire_date: editData.expire_date,
            issuer: editData.issuer,
          }),
          ...(editData.type === 'BANK' && {
            bankname: editData.bankname,
            iban: editData.iban,
            receipt_code: editData.receipt_code,
          }),
        }],
      };

      const res = await patchPayment(payload);
      if (res && res.error) {
        setSnackbar({ open: true, message: t('Error updating payment'), severity: 'error' });
      } else {
        // Refresh payment details
        const response = await getPayments(id);
        if (response && response[0]) {
          setPaymentDetails(response[0]);
        }
        setSnackbar({ open: true, message: t('Payment updated successfully'), severity: 'success' });
        setIsEditing(false);
      }
    } catch (err) {
      setSnackbar({ open: true, message: t('Error updating payment'), severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleEditFieldChange = (field, value) => {
    setEditData(prev => ({ ...prev, [field]: value }));
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleDeleteClick = () => {
    handleMenuClose();
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await deletePayment(id);
      setSnackbar({ open: true, message: t('Payment deleted successfully'), severity: 'success' });
      setDeleteDialogOpen(false);
      navigate('/payments');
    } catch (err) {
      console.error('Delete payment error:', err);
      setSnackbar({ open: true, message: t('Failed to delete payment'), severity: 'error' });
      setDeleteDialogOpen(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  const getPaymentTypeLabel = (type) => {
    const typeMap = {
      BANK: t('Bank'),
      CASH: t('Cash'),
      CHECK: t('Check'),
      CARD: t('Card'),
    };
    return typeMap[type] || type;
  };

  const getPaymentTypeStatus = (type) => {
    const statusMap = {
      CASH: 'paid',
      CHECK: 'pending',
      CARD: 'paid',
      BANK: 'paid',
    };
    return statusMap[type] || 'default';
  };

  const breadcrumbs = [
    { label: t('Payments'), path: '/payments' },
    { label: t('Payment Details') },
  ];

  if (loading) {
    return (
      <Box sx={{ py: 3 }}>
        <Container maxWidth={false}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 400,
            }}
          >
            <CircularProgress />
          </Box>
        </Container>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ py: 3 }}>
        <Container maxWidth={false}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/payments')}
          >
            {t('Back to Payments')}
          </Button>
        </Container>
      </Box>
    );
  }

  if (!paymentDetails) {
    return null;
  }

  const {
    bound,
    type,
    ammount,
    series,
    date,
    entity_name,
    entity_id,
    bankname,
    issuer,
    expire_date,
    iban,
    receipt_code,
    refcode,
  } = paymentDetails;

  return (
    <Box sx={{ py: 3 }}>
      <Container maxWidth="lg">
        <MuiPageHeader
          title={`${t('Payment')} #${id}`}
          subtitle={entity_name}
          breadcrumbs={breadcrumbs}
          actions={
            <Box sx={{ display: 'flex', gap: 1 }}>
              {isEditing ? (
                <>
                  <Button
                    variant="outlined"
                    startIcon={<CancelIcon />}
                    onClick={handleCancelEdit}
                    disabled={saving}
                  >
                    {t('Cancel')}
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<SaveIcon />}
                    onClick={handleSaveEdit}
                    disabled={saving}
                  >
                    {saving ? t('Saving...') : t('Save')}
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate('/payments')}
                  >
                    {t('Back')}
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<EditIcon />}
                    onClick={handleEdit}
                  >
                    {t('Edit')}
                  </Button>
                  <IconButton
                    aria-label="more options"
                    onClick={handleMenuClick}
                    sx={{ ml: 1 }}
                  >
                    <MoreVertIcon />
                  </IconButton>
                </>
              )}
            </Box>
          }
        />

        {/* More Options Menu */}
        <Menu
          anchorEl={anchorEl}
          open={menuOpen}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>{t('Delete Payment')}</ListItemText>
          </MenuItem>
        </Menu>

        <Grid container spacing={3}>
          {/* Payment Info Card */}
          <Grid item xs={12} md={8}>
            <MuiCard title={t('Payment Details')} showDivider>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <Grid container spacing={3}>
                  {/* Transaction Type */}
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">
                      {t('Transaction Type')}
                    </Typography>
                    {isEditing ? (
                      <ToggleButtonGroup
                        value={editData.bound}
                        exclusive
                        onChange={(e, val) => val && handleEditFieldChange('bound', val)}
                        size="small"
                        fullWidth
                        sx={{ mt: 0.5 }}
                      >
                        <ToggleButton value="Inbound" sx={{ '&.Mui-selected': { bgcolor: 'success.main', color: 'white', '&:hover': { bgcolor: 'success.dark' } } }}>
                          {t('Inbound')}
                        </ToggleButton>
                        <ToggleButton value="Outbound" sx={{ '&.Mui-selected': { bgcolor: 'error.main', color: 'white', '&:hover': { bgcolor: 'error.dark' } } }}>
                          {t('Outbound')}
                        </ToggleButton>
                      </ToggleButtonGroup>
                    ) : (
                      <Box sx={{ mt: 0.5 }}>
                        <MuiStatusChip
                          status={bound === 'Inbound' ? 'paid' : 'overdue'}
                          label={bound === 'Inbound' ? t('Inbound') : t('Outbound')}
                          showIcon
                        />
                      </Box>
                    )}
                  </Grid>

                  {/* Entity */}
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">
                      {bound === 'Inbound' ? t('Customer') : t('Supplier')}
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {entity_name}
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Divider />
                  </Grid>

                  {/* Payment Method */}
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">
                      {t('Payment Method')}
                    </Typography>
                    {isEditing ? (
                      <ToggleButtonGroup
                        value={editData.type}
                        exclusive
                        onChange={(e, val) => val && handleEditFieldChange('type', val)}
                        size="small"
                        fullWidth
                        sx={{ mt: 0.5 }}
                      >
                        <ToggleButton value="CASH" sx={{ '&.Mui-selected': { bgcolor: 'success.main', color: 'white', '&:hover': { bgcolor: 'success.dark' } } }}>
                          <AttachMoneyIcon fontSize="small" sx={{ mr: 0.5 }} />
                          {t('Cash')}
                        </ToggleButton>
                        <ToggleButton value="CHECK" sx={{ '&.Mui-selected': { bgcolor: 'success.main', color: 'white', '&:hover': { bgcolor: 'success.dark' } } }}>
                          <CheckBoxIcon fontSize="small" sx={{ mr: 0.5 }} />
                          {t('Check')}
                        </ToggleButton>
                        <ToggleButton value="CARD" sx={{ '&.Mui-selected': { bgcolor: 'success.main', color: 'white', '&:hover': { bgcolor: 'success.dark' } } }}>
                          <CreditCardIcon fontSize="small" sx={{ mr: 0.5 }} />
                          {t('Card')}
                        </ToggleButton>
                        <ToggleButton value="BANK" sx={{ '&.Mui-selected': { bgcolor: 'success.main', color: 'white', '&:hover': { bgcolor: 'success.dark' } } }}>
                          <AccountBalanceIcon fontSize="small" sx={{ mr: 0.5 }} />
                          {t('Bank')}
                        </ToggleButton>
                      </ToggleButtonGroup>
                    ) : (
                      <Box sx={{ mt: 0.5 }}>
                        <MuiStatusChip.Payment
                          status={getPaymentTypeStatus(type)}
                          label={getPaymentTypeLabel(type)}
                        />
                      </Box>
                    )}
                  </Grid>

                  {/* Amount */}
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">
                      {t('Amount')}
                    </Typography>
                    {isEditing ? (
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        value={editData.ammount}
                        onChange={(e) => handleEditFieldChange('ammount', e.target.value)}
                        sx={{ mt: 0.5 }}
                      />
                    ) : (
                      <Typography variant="h5" fontWeight={600} color="primary.main">
                        {Number(ammount).toFixed(2)}
                      </Typography>
                    )}
                  </Grid>

                  <Grid item xs={12}>
                    <Divider />
                  </Grid>

                  {/* Series */}
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">
                      {t('Series / Reference')}
                    </Typography>
                    {isEditing ? (
                      <TextField
                        fullWidth
                        size="small"
                        value={editData.series}
                        onChange={(e) => handleEditFieldChange('series', e.target.value)}
                        sx={{ mt: 0.5 }}
                      />
                    ) : (
                      <Typography variant="body1">
                        {series || '-'}
                      </Typography>
                    )}
                  </Grid>

                  {/* Date */}
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">
                      {t('Date')}
                    </Typography>
                    {isEditing ? (
                      <DatePicker
                        value={editData.date}
                        onChange={(val) => handleEditFieldChange('date', val)}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            size: 'small',
                            sx: { mt: 0.5 },
                          },
                        }}
                      />
                    ) : (
                      <Typography variant="body1">
                        {date ? new Date(date).toLocaleDateString() : '-'}
                      </Typography>
                    )}
                  </Grid>

                  {/* CHECK specific fields */}
                  {(type === 'CHECK' || (isEditing && editData.type === 'CHECK')) && (
                    <>
                      <Grid item xs={12}>
                        <Divider />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">
                          {t('Bank Name')}
                        </Typography>
                        {isEditing ? (
                          <TextField
                            fullWidth
                            size="small"
                            value={editData.bankname}
                            onChange={(e) => handleEditFieldChange('bankname', e.target.value)}
                            sx={{ mt: 0.5 }}
                          />
                        ) : (
                          <Typography variant="body1">
                            {bankname || '-'}
                          </Typography>
                        )}
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">
                          {t('Issuer')}
                        </Typography>
                        {isEditing ? (
                          <TextField
                            fullWidth
                            size="small"
                            value={editData.issuer}
                            onChange={(e) => handleEditFieldChange('issuer', e.target.value)}
                            sx={{ mt: 0.5 }}
                          />
                        ) : (
                          <Typography variant="body1">
                            {issuer || '-'}
                          </Typography>
                        )}
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">
                          {t('Expire Date')}
                        </Typography>
                        {isEditing ? (
                          <DatePicker
                            value={editData.expire_date}
                            onChange={(val) => handleEditFieldChange('expire_date', val)}
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                size: 'small',
                                sx: { mt: 0.5 },
                              },
                            }}
                          />
                        ) : (
                          <Typography variant="body1">
                            {expire_date ? new Date(expire_date).toLocaleDateString() : '-'}
                          </Typography>
                        )}
                      </Grid>
                    </>
                  )}

                  {/* BANK specific fields */}
                  {(type === 'BANK' || (isEditing && editData.type === 'BANK')) && (
                    <>
                      <Grid item xs={12}>
                        <Divider />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">
                          {t('Bank Name')}
                        </Typography>
                        {isEditing ? (
                          <TextField
                            fullWidth
                            size="small"
                            value={editData.bankname}
                            onChange={(e) => handleEditFieldChange('bankname', e.target.value)}
                            sx={{ mt: 0.5 }}
                          />
                        ) : (
                          <Typography variant="body1">
                            {bankname || '-'}
                          </Typography>
                        )}
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">
                          {t('IBAN')}
                        </Typography>
                        {isEditing ? (
                          <TextField
                            fullWidth
                            size="small"
                            value={editData.iban}
                            onChange={(e) => handleEditFieldChange('iban', e.target.value)}
                            sx={{ mt: 0.5 }}
                          />
                        ) : (
                          <Typography variant="body1">
                            {iban || '-'}
                          </Typography>
                        )}
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">
                          {t('Receipt Code')}
                        </Typography>
                        {isEditing ? (
                          <TextField
                            fullWidth
                            size="small"
                            value={editData.receipt_code}
                            onChange={(e) => handleEditFieldChange('receipt_code', e.target.value)}
                            sx={{ mt: 0.5 }}
                          />
                        ) : (
                          <Typography variant="body1">
                            {receipt_code || '-'}
                          </Typography>
                        )}
                      </Grid>
                    </>
                  )}

                  {/* Reference Code */}
                  {refcode && (
                    <>
                      <Grid item xs={12}>
                        <Divider />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">
                          {t('Account Reference')}
                        </Typography>
                        <Typography variant="body1" fontFamily="monospace">
                          {refcode}
                        </Typography>
                      </Grid>
                    </>
                  )}
                </Grid>
              </LocalizationProvider>
            </MuiCard>
          </Grid>

          {/* Quick Actions / Related Links */}
          <Grid item xs={12} md={4}>
            <MuiCard title={t('Quick Actions')} showDivider>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<ReceiptIcon />}
                  component={RouterLink}
                  to={entity_id ? `/invoices?entity=${entity_id}` : '/invoices'}
                  disabled={!entity_id}
                >
                  {t('View Invoices')}
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<ShoppingCartIcon />}
                  component={RouterLink}
                  to={entity_id ? `/orders?entity=${entity_id}` : '/orders'}
                  disabled={!entity_id}
                >
                  {t('View Orders')}
                </Button>
              </Box>
            </MuiCard>

            {/* Payment Summary */}
            <MuiCard title={t('Summary')} showDivider sx={{ mt: 3 }}>
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  {t('Total Amount')}
                </Typography>
                <Typography variant="h4" fontWeight={700} color="primary.main" sx={{ mb: 1 }}>
                  {Number(ammount).toFixed(2)}
                </Typography>
                <MuiStatusChip
                  status={bound === 'Inbound' ? 'paid' : 'overdue'}
                  label={bound === 'Inbound' ? t('Money Received') : t('Money Paid Out')}
                  variant="soft"
                />
              </Box>
            </MuiCard>
          </Grid>
        </Grid>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={handleDeleteCancel}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>{t('Delete Payment')}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {t('Are you sure you want to delete this payment? This action cannot be undone.')}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteCancel}>{t('Cancel')}</Button>
            <Button onClick={handleDeleteConfirm} color="error" variant="contained">
              {t('Delete')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for feedback */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

MuiPaymentOverview.propTypes = {};

export default MuiPaymentOverview;
